/**
 * `modelPolicyApi` — RTK Query slice for the org model policy
 * (proxy-server `GET`/`PUT /api/v1/ontology/model-policy`). Backs the
 * Settings → Model Selection card and the chat composer's model selector.
 * URLs are relative (resolved against `ontologyBaseQuery`'s
 * `/api/v1/ontology` root).
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { ModelTaskClass } from '@atbash-labs/constants'

import { ontologyBaseQuery } from '../core'
import type {
  CompactionDefaults,
  ModelCandidate,
  ModelDeployment,
  ModelEffort,
  ModelOption,
  ModelProvider,
  ModelTarget,
  OrganizationModelPolicy,
  TaskClassRoutingPolicy,
  TaskClassRoutingPolicyPatch,
  TaskClassRoutingState,
  UpdateOrganizationModelPolicyPayload
} from './types'

type ModelOptionWire = Omit<ModelOption, 'effort_levels' | 'supports_effort'> & {
  effort_levels?: ModelEffort[]
  supports_effort?: boolean
}

interface TaskClassRoutingPolicyWire {
  candidates: ModelCandidate[]
}

interface TaskClassRoutingStateWire {
  default_policy: TaskClassRoutingPolicyWire
  effective_policy: TaskClassRoutingPolicyWire
  resolved_target: ModelTarget | null
  resolved_effort: ModelEffort | null
}

interface OrganizationModelPolicyWire {
  providers: ModelProvider[]
  catalog: ModelOptionWire[]
  allowed_models?: ModelTarget[]
  model_deployments?: ModelDeployment[]
  task_class_routing: Record<ModelTaskClass, TaskClassRoutingStateWire>
  fallback_enabled: boolean
  auto_compaction_trigger_input_tokens: number | null
  auto_compaction_trigger_fraction?: number | null
  compaction_defaults?: {
    trigger_input_tokens: number
    /** Absent on proxies that predate the window-share default. */
    trigger_fraction?: number
    context_budget_fraction: number
    min_trigger_input_tokens: number
    max_trigger_input_tokens: number
    min_trigger_fraction: number
    max_trigger_fraction: number
  }
}

/** Product defaults assumed when a proxy predates `compaction_defaults`. */
export const FALLBACK_COMPACTION_DEFAULTS: CompactionDefaults = {
  triggerInputTokens: 150_000,
  triggerFraction: 0.75,
  contextBudgetFraction: 0.8,
  minTriggerInputTokens: 50_000,
  maxTriggerInputTokens: 800_000,
  minTriggerFraction: 0.1,
  maxTriggerFraction: 0.9
}

interface TaskClassRoutingPolicyPatchWire {
  candidates: ModelCandidate[]
}

interface UpdateOrganizationModelPolicyPayloadWire {
  task_class_routing?: Partial<Record<ModelTaskClass, TaskClassRoutingPolicyPatchWire | null>>
  allowed_models?: ModelTarget[]
  model_deployments?: ModelDeployment[]
  fallback_enabled?: boolean
  auto_compaction_trigger_input_tokens?: number | null
  auto_compaction_trigger_fraction?: number | null
}

function routingPolicyFromWire(policy: TaskClassRoutingPolicyWire): TaskClassRoutingPolicy {
  return {
    candidates: policy.candidates
  }
}

export function organizationModelPolicyFromWire(
  policy: OrganizationModelPolicyWire
): OrganizationModelPolicy {
  const taskClassRouting = {} as Record<ModelTaskClass, TaskClassRoutingState>
  for (const taskClass of Object.values(ModelTaskClass)) {
    const state = policy.task_class_routing[taskClass]
    taskClassRouting[taskClass] = {
      defaultPolicy: routingPolicyFromWire(state.default_policy),
      effectivePolicy: routingPolicyFromWire(state.effective_policy),
      resolvedTarget: state.resolved_target,
      resolvedEffort: state.resolved_effort ?? null
    }
  }
  return {
    providers: policy.providers,
    catalog: policy.catalog.map((option) => ({
      ...option,
      effort_levels: option.effort_levels ?? [],
      supports_effort: (option.effort_levels?.length ?? 0) > 0 || option.supports_effort === true
    })),
    allowedModels:
      policy.allowed_models ??
      policy.catalog.map((option) => ({
        provider: option.provider,
        model: option.model
      })),
    modelDeployments: policy.model_deployments ?? [],
    taskClassRouting,
    fallback_enabled: policy.fallback_enabled,
    auto_compaction_trigger_input_tokens: policy.auto_compaction_trigger_input_tokens,
    auto_compaction_trigger_fraction: policy.auto_compaction_trigger_fraction ?? null,
    compactionDefaults:
      policy.compaction_defaults === undefined
        ? FALLBACK_COMPACTION_DEFAULTS
        : {
            triggerInputTokens: policy.compaction_defaults.trigger_input_tokens,
            triggerFraction:
              policy.compaction_defaults.trigger_fraction ??
              FALLBACK_COMPACTION_DEFAULTS.triggerFraction,
            contextBudgetFraction: policy.compaction_defaults.context_budget_fraction,
            minTriggerInputTokens: policy.compaction_defaults.min_trigger_input_tokens,
            maxTriggerInputTokens: policy.compaction_defaults.max_trigger_input_tokens,
            minTriggerFraction: policy.compaction_defaults.min_trigger_fraction,
            maxTriggerFraction: policy.compaction_defaults.max_trigger_fraction
          }
  }
}

function routingPolicyPatchToWire(
  patch: TaskClassRoutingPolicyPatch
): TaskClassRoutingPolicyPatchWire {
  return { candidates: patch.candidates }
}

export function updateOrganizationModelPolicyPayloadToWire(
  payload: UpdateOrganizationModelPolicyPayload
): UpdateOrganizationModelPolicyPayloadWire {
  let taskClassRouting:
    | Partial<Record<ModelTaskClass, TaskClassRoutingPolicyPatchWire | null>>
    | undefined
  if (payload.taskClassRouting !== undefined) {
    taskClassRouting = {}
    for (const [taskClass, patch] of Object.entries(payload.taskClassRouting)) {
      taskClassRouting[taskClass as ModelTaskClass] =
        patch === null ? null : routingPolicyPatchToWire(patch)
    }
  }
  return {
    ...(taskClassRouting === undefined ? {} : { task_class_routing: taskClassRouting }),
    ...(payload.allowedModels === undefined ? {} : { allowed_models: payload.allowedModels }),
    ...(payload.modelDeployments === undefined
      ? {}
      : { model_deployments: payload.modelDeployments }),
    ...(payload.fallback_enabled === undefined
      ? {}
      : { fallback_enabled: payload.fallback_enabled }),
    ...(payload.auto_compaction_trigger_input_tokens === undefined
      ? {}
      : {
          auto_compaction_trigger_input_tokens: payload.auto_compaction_trigger_input_tokens
        }),
    ...(payload.auto_compaction_trigger_fraction === undefined
      ? {}
      : { auto_compaction_trigger_fraction: payload.auto_compaction_trigger_fraction })
  }
}

export const ORGANIZATION_MODEL_POLICY_TAG_TYPES = ['OrganizationModelPolicy'] as const

export const modelPolicyApi = createApi({
  reducerPath: 'modelPolicyApi',
  baseQuery: ontologyBaseQuery,
  tagTypes: ORGANIZATION_MODEL_POLICY_TAG_TYPES,
  endpoints: (builder) => ({
    getModelPolicy: builder.query<OrganizationModelPolicy, void>({
      query: () => '/model-policy',
      transformResponse: organizationModelPolicyFromWire,
      providesTags: [{ type: 'OrganizationModelPolicy', id: 'CURRENT' }]
    }),
    updateModelPolicy: builder.mutation<
      OrganizationModelPolicy,
      UpdateOrganizationModelPolicyPayload
    >({
      query: (body) => ({
        url: '/model-policy',
        method: 'PUT',
        body: updateOrganizationModelPolicyPayloadToWire(body)
      }),
      transformResponse: organizationModelPolicyFromWire,
      invalidatesTags: [{ type: 'OrganizationModelPolicy', id: 'CURRENT' }]
    })
  })
})

export const { useGetModelPolicyQuery, useUpdateModelPolicyMutation } = modelPolicyApi
